/**
 * ApprovalExecutionContext.
 *
 * Exposes the `execution_id` of the AttentionItem backing an approval
 * card to its descendants — most importantly, to section renderers
 * (`ExecutionSummaryRenderer`) that need to make on-demand API calls
 * tied to that execution.
 *
 * Why a context rather than a prop:
 *
 * The renderer registry contract (`RendererProps = { data: unknown }`,
 * see `renderers/registry.tsx`) is intentionally narrow — every
 * renderer accepts exactly one `data` prop. Threading `executionId`
 * through the registry would either (a) widen `RendererProps` for
 * every renderer, even those that don't care, or (b) smuggle
 * `executionId` inside `data`, mixing presentational state with
 * server data. A context keeps the renderer surface clean while still
 * letting one specific renderer (`ExecutionSummaryRenderer`) reach
 * the execution id when it needs to call `/v1/executions/{id}/summarise`.
 *
 * `executionId` is `null` whenever the surrounding attention item is
 * not backed by an execution (or before the detail payload has
 * loaded). Consumers should treat `null` as "summarisation not
 * available" and degrade gracefully.
 */

import * as React from 'react'

export interface ApprovalExecutionContextValue {
  executionId: string | null
}

const ApprovalExecutionContext =
  React.createContext<ApprovalExecutionContextValue>({
    executionId: null,
  })

export function ApprovalExecutionProvider({
  executionId,
  children,
}: {
  executionId: string | null
  children: React.ReactNode
}) {
  // Memoised so consumers don't re-render on every parent render when
  // the execution id is stable across renders (the common case).
  const value = React.useMemo(() => ({ executionId }), [executionId])
  return (
    <ApprovalExecutionContext.Provider value={value}>
      {children}
    </ApprovalExecutionContext.Provider>
  )
}

export function useApprovalExecutionContext(): ApprovalExecutionContextValue {
  return React.useContext(ApprovalExecutionContext)
}
